import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { isIncluded } from '../../../../utils/variables';
import { useSharedView } from '../../../hooks/use-shared-view';
import { GeodataSetupContext, GeodataSetupPage } from '../../../state/geodata-setup-state';
import { Button } from '../../button/button';

export const GeodataSetupFooterButtons = () => {
  const { t } = useTranslation();
  const context = useContext(GeodataSetupContext);
  const { isSharedView } = useSharedView();

  const pageSequence: GeodataSetupPage[] = useMemo(() => {
    if (context?.gcpEnabled === false) {
      return ['processType', 'settings', 'boundary', 'images', 'summary', 'finish'];
    }

    const sequence: GeodataSetupPage[] = context?.useInteractiveSetup
      ? ['processType', 'settings', 'gcp', 'boundary', 'addons', 'summary', 'finish']
      : ['processType', 'settings', 'gcp', 'boundary', 'addons', 'images', 'summary', 'finish'];

    // Addons are not part of the flow for process types without addon support (e.g. PM).
    return context?.addonsEnabled === false
      ? sequence.filter((page) => page !== 'addons')
      : sequence;
  }, [context]);

  const showPageButtons = useMemo(() => {
    return isIncluded(
      ['settings', 'gcp', 'boundary', 'addons', 'images', 'summary'],
      context?.page,
    );
  }, [context]);

  useEffect(() => {
    context?.setAbortConfirmed(false);
  }, [context?.page]); // eslint-disable-line react-hooks/exhaustive-deps

  const goToNextPage = useCallback(() => {
    const currentIndex = pageSequence.findIndex((page) => page === context?.page);
    if (currentIndex === -1) {
      return;
    }
    const nextIndex = currentIndex + 1;
    if (nextIndex >= pageSequence.length) {
      return;
    }
    context?.setPage(pageSequence[nextIndex]);
  }, [context, pageSequence]);

  const goToPreviousPage = useCallback(() => {
    const currentIndex = pageSequence.findIndex((page) => page === context?.page);
    if (currentIndex === -1) {
      context?.setPage(pageSequence[0]);
    }
    const previousIndex = currentIndex - 1;
    if (previousIndex < 0) {
      return;
    }
    context?.setPage(pageSequence[previousIndex]);
  }, [context, pageSequence]);

  const nextButtonLabel = useMemo(() => {
    return context?.page === 'summary'
      ? t('confirm', { ns: 'common' })
      : t('next', { ns: 'common' });
  }, [context, t]);

  const closeButtonLabel = useMemo(() => {
    return context?.page === 'finish'
      ? t('close', { ns: 'common' })
      : t('cancel', { ns: 'common' });
  }, [context, t]);

  const closeDisabled =
    context?.page === 'finish' && !context.isFinishComplete && !context.abortConfirmed;

  return (
    <>
      {showPageButtons && (
        <>
          <Button
            color="secondary"
            leftIcon={{ icon: ['fad', 'arrow-left'] }}
            variant="contained"
            onClick={goToPreviousPage}
          >
            {t('previous', { ns: 'common' })}
          </Button>
          <Button
            color="primary"
            disabled={context?.disableNextButton}
            leftIcon={{ icon: ['fad', 'arrow-right'] }}
            variant="contained"
            onClick={goToNextPage}
          >
            {nextButtonLabel}
          </Button>
        </>
      )}
      {context?.closeDialog && !isSharedView && (
        <Button disabled={closeDisabled} variant="text" onClick={context.closeDialog}>
          {closeButtonLabel}
        </Button>
      )}
    </>
  );
};
